import { ClipboardEvent, KeyboardEvent, useEffect, useState } from 'react';

import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  isValidEmail,
  joinEmailList,
  splitEmailList,
} from '@/modules/common/validations/email-list-schema';

const COMMIT_KEYS = new Set(['Enter', ',', ';']);
const EMAILS_SEPARATOR_PATTERN = /[,;\s]+/;

const parseEmails = (text: string): string[] =>
  text
    .split(EMAILS_SEPARATOR_PATTERN)
    .map(email => email.trim())
    .filter(email => email.length > 0);

const unique = (emails: string[]): string[] => [...new Set(emails)];

interface MultiEmailInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

/** The value stays a comma-separated string; typed text is part of it before it becomes a chip. */
export function MultiEmailInput({
  value,
  onChange,
  onBlur,
  disabled = false,
  placeholder,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: MultiEmailInputProps) {
  const { t } = useTranslation('common');
  const [draft, setDraft] = useState('');

  const entries = splitEmailList(value).filter(email => email.length > 0);
  const trimmedDraft = draft.trim();
  const isDraftInValue = trimmedDraft !== '' && entries[entries.length - 1] === trimmedDraft;
  const chips = isDraftInValue ? entries.slice(0, -1) : entries;

  // The value was replaced from outside (e.g. a form reset): the typed text no longer belongs to it
  useEffect(() => {
    if (trimmedDraft !== '' && !isDraftInValue) setDraft('');
  }, [trimmedDraft, isDraftInValue]);

  const emit = (nextChips: string[], nextDraft: string) => {
    const next = joinEmailList(nextDraft.trim() ? [...nextChips, nextDraft.trim()] : nextChips);
    if (next !== value) onChange(next);
  };

  const changeDraft = (text: string) => {
    setDraft(text);
    emit(chips, text);
  };

  const commit = (text: string) => {
    setDraft('');
    emit(unique([...chips, ...parseEmails(text)]), '');
  };

  const remove = (index: number) => {
    emit(
      chips.filter((_, chipIndex) => chipIndex !== index),
      draft
    );
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (COMMIT_KEYS.has(event.key)) {
      event.preventDefault();
      commit(draft);
      return;
    }
    if (event.key === 'Backspace' && draft === '' && chips.length > 0) {
      remove(chips.length - 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const input = event.currentTarget;
    const selectionStart = input.selectionStart ?? draft.length;
    const selectionEnd = input.selectionEnd ?? draft.length;
    const text =
      draft.slice(0, selectionStart) +
      event.clipboardData.getData('text') +
      draft.slice(selectionEnd);

    if (EMAILS_SEPARATOR_PATTERN.test(text.trim())) {
      commit(text);
      return;
    }
    changeDraft(text);
  };

  const emptyPlaceholder = disabled ? t('multiEmailInput.empty') : placeholder;

  return (
    <div
      className={cn(
        'flex min-h-10 w-full flex-wrap items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-base ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 md:text-sm',
        disabled && 'cursor-not-allowed bg-muted',
        ariaInvalid && 'border-destructive'
      )}
    >
      {chips.map((email, index) => (
        <Badge
          key={`${index}-${email}`}
          data-email-chip=""
          variant={isValidEmail(email) ? 'secondary' : 'destructiveOutline'}
          className="max-w-full gap-1 font-normal"
        >
          <span className="min-w-0 truncate" title={email}>
            {email}
          </span>
          {!disabled && (
            <button
              type="button"
              className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('multiEmailInput.remove', { email })}
              onClick={() => remove(index)}
            >
              <XIcon className="h-3 w-3" />
            </button>
          )}
        </Badge>
      ))}
      <input
        id={id}
        value={draft}
        disabled={disabled}
        placeholder={chips.length === 0 ? emptyPlaceholder : undefined}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        onChange={event => changeDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={() => {
          commit(draft);
          onBlur?.();
        }}
      />
    </div>
  );
}
